import { computed, Injectable, signal, WritableSignal } from '@angular/core';
import { haveDatesChanged, isEndBeforeStart } from '../utils/date-comparison.utils';
import { endOfDay, startOfDay } from '../utils/date-range-filter.utils';
import { addInterval, DateInterval, detectInterval, subtractInterval } from '../utils/date-range.utils';

@Injectable()
export class DateNavigationService {
  readonly startDate: WritableSignal<Date | null> = signal(null);
  readonly endDate: WritableSignal<Date | null> = signal(null);

  // The pickers stage a window; only applyDates() (the refresh action) makes it the one
  // data is fetched with. Data loaders must read the applied window, never the pickers,
  // so grid-initiated refetches (sort, filter, scroll) can't silently adopt staged dates.
  private readonly appliedRange = signal<{ startDate: Date; endDate: Date } | null>(null);

  // A picker names a day, but every data endpoint filters on instants (BETWEEN dateFrom AND dateTo).
  // The window is therefore widened to whole days on the way out, or the end day is silently lost:
  // a value recorded at 02:00 on the end day falls after a midnight bound, so asking for
  // 01.07 -> 13.07 returns nothing from 13.07 at all.
  readonly appliedStartDate = computed(() => {
    const range = this.appliedRange();
    return range ? startOfDay(range.startDate) : null;
  });
  readonly appliedEndDate = computed(() => {
    const range = this.appliedRange();
    return range ? endOfDay(range.endDate) : null;
  });
  readonly hasAppliedDates = computed(() => this.appliedRange() !== null);
  // Compares the pickers against the raw applied values, never the widened ones, or applying a
  // window would instantly report itself as pending and pin the stale-data warning on screen.
  readonly hasPendingChanges = computed(() => {
    const range = this.appliedRange();
    return haveDatesChanged(this.startDate(), this.endDate(), range?.startDate ?? null, range?.endDate ?? null);
  });

  readonly dateInterval = computed<DateInterval | null>(() => {
    const start = this.startDate();
    const end = this.endDate();
    if (!start || !end || start >= end) return null;
    return detectInterval(start, end);
  });

  readonly canNavigate = computed(() => {
    const start = this.startDate();
    const end = this.endDate();
    return start !== null && end !== null && start < end;
  });

  readonly endDateDisabled = computed(() => this.startDate() === null);

  navigate(direction: -1 | 1): void {
    const interval = this.dateInterval();
    const start = this.startDate();
    const end = this.endDate();
    if (!interval || !start || !end) return;

    const fn = direction === 1 ? addInterval : subtractInterval;
    this.startDate.set(fn(start, interval));
    this.endDate.set(fn(end, interval));
  }

  navigatePrevious(): void {
    this.navigate(-1);
  }

  navigateNext(): void {
    this.navigate(1);
  }

  setDates(start: Date | null, end: Date | null): void {
    this.startDate.set(start);
    this.endDate.set(end);
  }

  applyDates(): void {
    const start = this.startDate();
    const end = this.endDate();
    // An inverted picker window is a transient entry error, never a window to fetch with.
    if (isEndBeforeStart(start, end)) return;
    this.appliedRange.set(start && end ? { startDate: start, endDate: end } : null);
  }

  clearAppliedDates(): void {
    this.appliedRange.set(null);
  }
}
